/**
 * HTTP client for the Virtuosa Symfony REST API.
 *
 * Replaces direct analyzer calls, all requests go through the Symfony API
 * which handles storage, authentication, quotas, and proxies to the analyzer.
 */

import type {
  AnalysisResult,
  GenerateResult,
  ApiSongResponse,
  InspectResult,
} from "../music/analyzer-types";
import { getToken } from "./auth";
import type { AuthResponse, VirtuosaUser } from "./auth";

/**
 * Base URL prepended to every API call.
 *
 * Defaults to `""` so requests stay same-origin and hit the Next.js catch-all
 * proxy at `app/api/[...path]/route.ts`, which forwards to the Symfony API.
 * Override with `NEXT_PUBLIC_API_BASE_URL` only when the front is loaded
 * outside the Next host (e.g. iOS/Capacitor build) and needs an absolute URL
 * pointing at the Next server.
 */
const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

// ── Error ──────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: string,
    public readonly type?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function assertOk(response: Response): Promise<void> {
  if (response.ok) {
    return;
  }

  const body = await response.text().catch(() => undefined);
  let message = `API error: ${response.status} ${response.statusText}`;
  let type: string | undefined;

  // Try to parse RFC 7807 error
  try {
    if (body) {
      const err = JSON.parse(body);

      if (err.detail) {
        message = err.detail;
      }

      if (err.type) {
        type = err.type;
      }
    }
  } catch {
    // not JSON
  }

  throw new ApiError(message, response.status, body, type);
}

function authHeaders(): Record<string, string> {
  const token = getToken();

  if (token) {
    return { Authorization: `Bearer ${token}` };
  }

  return {};
}

// ── Auth ───────────────────────────────────────────────────────

/**
 * Le formulaire ne demande que l'e-mail et le mot de passe. Le nom affichable
 * est dérivé de l'adresse côté serveur (`Domain\Account\DisplayName`) : un
 * champ « Nom » coûte une micro-décision — prénom ? pseudo ? — pour une donnée
 * que rien n'affiche au premier jet.
 */
export async function register(
  email: string,
  password: string,
): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  await assertOk(response);

  return response.json();
}

export async function login(
  email: string,
  password: string,
): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  await assertOk(response);

  return response.json();
}

/**
 * « Cette adresse a-t-elle un compte ? »
 *
 * Première étape de l'écran de compte : on saisit son adresse, seule, et
 * l'écran suivant demande soit le mot de passe, soit d'en choisir un. La
 * personne n'a jamais à déclarer si elle est nouvelle.
 *
 * La réponse révèle l'existence d'un compte — c'est assumé, et plafonné côté
 * serveur, sans quoi la route serait un oracle d'énumération scriptable.
 */
export async function lookupEmail(email: string): Promise<boolean> {
  const response = await fetch(`${API_BASE_URL}/api/auth/lookup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  await assertOk(response);

  const payload: { known: boolean } = await response.json();

  return payload.known;
}

/**
 * Ouvre une session sans avoir demandé si la personne est nouvelle.
 *
 * C'est le serveur qui tranche : `known: false` veut dire « aucun compte sur
 * cette adresse », et le front bascule alors sur son écran de confirmation de
 * création plutôt que d'afficher une erreur.
 *
 * Un `ApiError` 401 signifie « le compte existe, le mot de passe est faux » —
 * et à cet instant on peut le dire sans rien révéler de plus, puisqu'on vient
 * de l'établir.
 */
export async function openSession(
  email: string,
  password: string,
): Promise<AuthResponse | { known: false }> {
  const response = await fetch(`${API_BASE_URL}/api/auth/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (response.status === 404) {
    return { known: false };
  }

  await assertOk(response);

  return response.json();
}

export async function fetchMe(): Promise<VirtuosaUser> {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    headers: authHeaders(),
  });
  await assertOk(response);

  return response.json();
}

// ── Songs: Upload MIDI ───────────────────────────────────────

export type UploadMidiOptions = {
  title?: string;
  composer?: string;
  /** Transpose every note by N semitones (range: -24..+24, 0 = original). */
  transpose?: number;
  /**
   * Real MIDI track indices to keep (from the manual `needs_help` selection).
   * When provided, the analyzer keeps and re-hands only those tracks instead
   * of trusting the auto-detected piano roles. Omitted = auto-detection.
   */
  tracks?: number[];
  /**
   * Original filename slug to forward to the backend so that
   * `TitleNormalizer::confirm` writes the user-validated entry under the same
   * cache key as the earlier `inspectMidi` suggestion. Defaults to `file.name`.
   */
  filename?: string;
};

export type SongUploadResponse = ApiSongResponse;

export type InspectMidiOptions = {
  /**
   * Override the filename sent to the title normalizer. Defaults to
   * `file.name`. Useful when the local `File` was renamed/cleaned client-side
   * but we still want the normalizer to see the original slug.
   */
  filename?: string;
};

/**
 * Run a lightweight inspect on a MIDI file (metadata, tempo, tracks, key)
 * before deciding to upload it. Routes through the Symfony API so it benefits
 * from the same auth and proxy as the upload flow.
 *
 * Also returns title-normalization suggestions (see
 * docs/spec/title-normalization-spec.md §4): the response carries
 * `suggestedTitle`, `suggestedComposer`, `titleConfidence`, `titleSource`.
 */
export async function inspectMidi(
  file: File,
  options?: InspectMidiOptions,
): Promise<InspectResult> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("filename", options?.filename ?? file.name);

  const response = await fetch(`${API_BASE_URL}/api/library/inspect`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  await assertOk(response);

  return response.json();
}

export async function uploadMidi(
  file: File,
  options?: UploadMidiOptions,
): Promise<SongUploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("filename", options?.filename ?? file.name);

  if (options?.title) {
    formData.append("title", options.title);
  }

  if (options?.composer) {
    formData.append("composer", options.composer);
  }

  if (options?.transpose) {
    formData.append("transpose", String(options.transpose));
  }

  if (options?.tracks && options.tracks.length > 0) {
    for (const trackIndex of options.tracks) {
      formData.append("tracks[]", String(trackIndex));
    }
  }

  const response = await fetch(`${API_BASE_URL}/api/songs/midi`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  await assertOk(response);

  return response.json();
}

// ── Songs: Upload Sheet Music ────────────────────────────────

export type UploadSheetMusicOptions = {
  title: string;
  composer?: string;
  tempo?: number;
  /** Transpose every note by N semitones (range: -24..+24, 0 = original). */
  transpose?: number;
};

export async function uploadSheetMusic(
  images: File[],
  options: UploadSheetMusicOptions,
): Promise<SongUploadResponse> {
  const formData = new FormData();
  const isPdf = images.length === 1 && images[0].type === "application/pdf";

  for (let i = 0; i < images.length; i++) {
    formData.append("files[]", images[i]);

    if (!isPdf) {
      formData.append("pageNumbers[]", String(i));
    }
  }

  formData.append("title", options.title);

  if (options.composer) {
    formData.append("composer", options.composer);
  }

  if (options.tempo) {
    formData.append("tempo", String(options.tempo));
  }

  if (options.transpose) {
    formData.append("transpose", String(options.transpose));
  }

  const response = await fetch(`${API_BASE_URL}/api/songs/images`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  await assertOk(response);

  return response.json();
}

// ── Library: Transpose MIDI (stateless re-analysis) ──────────

/**
 * Re-analyze an already-stored MIDI file with a transposition offset.
 * Returns a fresh AnalysisResult, does NOT create a new library entry.
 */
export async function transposeMidi(
  midiFileId: string,
  semis: number,
): Promise<AnalysisResult> {
  const response = await fetch(
    `${API_BASE_URL}/api/library/midi/${midiFileId}/transpose?semis=${semis}`,
    {
      method: "POST",
      headers: authHeaders(),
    },
  );
  await assertOk(response);

  return response.json();
}

// ── Library: Get Analysis ─────────────────────────────────────

export async function getAnalysis(
  type: "midi" | "score",
  id: string,
): Promise<AnalysisResult> {
  const response = await fetch(
    `${API_BASE_URL}/api/library/${type}/${id}/analysis`,
    { headers: authHeaders() },
  );
  await assertOk(response);
  const result = await response.json();
  return result.data;
}

// ── Compte : le nom qu'on veut bien montrer ───────────────────

/**
 * Change, ou efface, le nom affiché lors d'un partage.
 *
 * Passer `null` ou une chaîne vide **efface** : vider le champ est le geste
 * naturel de « je ne veux plus qu'on m'affiche », et l'absence de nom est un
 * état parfaitement valide. L'aperçu d'un lien dit alors « Un morceau a été
 * partagé avec toi ».
 *
 * Rend l'utilisateur à jour, que l'appelant range dans sa session : c'est ce
 * qui évite de recharger la page pour voir son propre nom changer.
 */
export async function updateNickname(nickname: string | null): Promise<VirtuosaUser> {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ nickname }),
  });
  await assertOk(response);

  return response.json();
}

/**
 * Ferme un compte, définitivement.
 *
 * L'adresse est renvoyée au serveur, qui la compare à celle du compte : ce
 * n'est pas une authentification, le jeton l'a déjà faite, c'est la preuve que
 * la destruction est voulue. Une suppression physique ne doit pas pouvoir
 * partir d'un clic mal placé ni d'une requête rejouée.
 *
 * Ne rend rien : après elle, le jeton ne désigne plus personne. L'appelant
 * efface l'état local avant de recharger, sinon la progression du disparu
 * resterait à l'écran.
 */
export async function deleteAccount(email: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ email }),
  });
  await assertOk(response);
}

// ── Library: List ─────────────────────────────────────────────

/**
 * D'où vient une entrée de bibliothèque.
 *
 * `shared` n'est pas un troisième mot pour « je l'ai » : c'est le seul cas où
 * `sharedBy` est renseigné, et le seul que `filter=shared` remonte.
 */
export type LibrarySource = "import" | "catalog" | "shared";

/** Qui a partagé le morceau. Un nom affichable, jamais l'adresse e-mail. */
export type LibrarySharedBy = {
  id: string;
  /**
   * Le nom que la personne a choisi, `null` tant qu'elle n'en a pas choisi.
   *
   * C'est le seul nom que le produit montre à quelqu'un d'autre. Il portait
   * autrefois une étiquette dérivée de l'adresse e-mail, que personne n'avait
   * choisi d'exposer et que personne ne pouvait changer ; l'aperçu d'un lien de
   * partage l'affichait pourtant à des inconnus.
   *
   * Sur son propre écran, `null` se replie sur l'adresse. Chez les autres, sur
   * rien du tout.
   */
  nickname: string | null;
};

/**
 * Une ligne de « Mes morceaux ». Miroir de `LibraryEntryResponse`.
 *
 * `midiFileId` est ce que le studio ouvre, et aussi la clé sous laquelle le
 * parcours d'apprentissage range sa progression — pour un import comme pour un
 * morceau du catalogue.
 *
 * # Les champs plats doublent `piece`, et c'est voulu
 *
 * `title`, `composer` et `difficulty` sont aussi dans `piece`. Les retirer
 * casserait `hydrate-progress.ts`, qui lit une entrée pour retrouver le dernier
 * morceau travaillé et n'a que faire d'une fiche de catalogue. Une carte, elle,
 * lit `piece` : c'est là que sont la silhouette et le slug.
 */
export type LibraryEntryItem = {
  id: string;
  /** L'œuvre. */
  pieceId: string;
  /** Le fichier de cette ligne : c'est lui qu'on rouvre, range et partage. */
  scoreId: string;
  midiFileId: string | null;
  title: string;
  composer: string | null;
  difficulty: number | null;
  source: LibrarySource;
  /** `false` quand le fichier n'a pas de source jouable : la ligne reste, le clic non. */
  openable: boolean;
  addedAt: string;
  lastOpenedAt: string;
  /**
   * La fiche complète, pour rendre la même carte qu'au catalogue sans un
   * second appel par ligne.
   */
  piece: CatalogPiece;
  /** Renseigné sur la seule source `shared`. */
  sharedBy: LibrarySharedBy | null;
  /**
   * Où en est le travail, tel que le client l'a résumé — voir « Progression du
   * parcours » plus bas. `null` tant que rien n'a été travaillé, ou tant que ce
   * compte n'a pas poussé de résumé (`backfillProgressSummaries`).
   */
  practice: LibraryPractice | null;
  /** Les playlists de la personne qui contiennent cette pièce. */
  playlistIds: string[];
  /**
   * La forme sous laquelle la personne l'a déposé. `null` pour le catalogue et
   * les partages, qui ne sont pas des imports.
   */
  importFormat: ImportFormat | null;
};

export type ImportFormat = "midi" | "musicxml" | "photo";

export type PaginatedResult<T> = {
  items: T[];
  meta: { page: number; limit: number; total: number; totalPages: number };
};

/**
 * Les quatre vues de « Ma bibliothèque ». Chacune a son écran, et c'est le
 * serveur qui les définit : `in-progress` lit le résumé de progression, que le
 * navigateur ne peut pas filtrer sans descendre toute la liste.
 */
/**
 * Les vues de « Ma bibliothèque », miroir de `LibraryFilter` côté API.
 *
 * `in-progress`, `revisit` et `played` sont les trois états de travail pris un
 * par un ; `practiced` les rend tous les trois. C'est ce dernier que sert « Là
 * où tu t'es arrêté », qui regroupe ensuite ses trois blocs à partir de
 * `entry.practice` : un appel par bloc paierait trois fois la pagination pour
 * trois listes courtes par construction.
 *
 * Un parcours mené transposé range sa progression sous une clé suffixée
 * (`<midiFileId>:t<n>`) et ne remonte dans aucun de ces filtres. Rien plutôt
 * qu'un état emprunté à une autre tonalité, cf. `journey-glance.ts`.
 */
export type LibraryFilter =
  | "all"
  | "in-progress"
  | "revisit"
  | "played"
  | "practiced"
  | "shared"
  | "imports";

/** `recent` = dernière ouverture, `added` = entrée dans la bibliothèque. */
export type LibrarySort = "recent" | "added" | "title";

export type ListLibraryParams = {
  /** Défaut serveur : `all`. */
  readonly filter?: LibraryFilter;
  /** Défaut serveur : `recent`. */
  readonly sort?: LibrarySort;
  /** Identifiant de playlist : restreint la liste à son contenu. */
  readonly playlist?: string;
  /** Titre ou compositeur, sans casse ni accents — comme au catalogue. */
  readonly search?: string;
  readonly page?: number;
  readonly limit?: number;
};

export async function listLibrary(
  params: ListLibraryParams = {},
): Promise<PaginatedResult<LibraryEntryItem>> {
  const query = new URLSearchParams({
    page: String(params.page ?? 1),
    limit: String(params.limit ?? 20),
  });

  // Les critères absents ne sont pas envoyés : le serveur porte déjà ses
  // défauts, et les recopier ici en ferait une seconde définition à tenir.
  if (params.filter !== undefined) {
    query.set("filter", params.filter);
  }

  if (params.sort !== undefined) {
    query.set("sort", params.sort);
  }

  if (params.playlist !== undefined) {
    query.set("playlist", params.playlist);
  }

  if (params.search !== undefined && params.search !== "") {
    query.set("search", params.search);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/library?${query}`,
    { headers: authHeaders() },
  );
  await assertOk(response);

  return response.json();
}

/**
 * Le résultat d'un geste idempotent : ce que l'API rend, et si elle vient de le
 * créer.
 *
 * L'API distingue volontairement `201` de `200` sur « ajouter à ma
 * bibliothèque », « créer un lien » et « ajouter à une playlist ». Écraser les
 * deux en une seule valeur ferait perdre la seule information qui les sépare —
 * celle qui permet d'écrire « ajouté » plutôt que « déjà là », sans relire la
 * liste pour le deviner.
 */
export type IdempotentWrite<T> = {
  /** `true` sur un `201`, `false` sur un `200`. */
  readonly created: boolean;
  readonly item: T;
};

/**
 * Le fichier visé par un geste de bibliothèque.
 *
 * `scoreId` quand on sait lequel : une ligne de « Mes morceaux », le morceau
 * ouvert dans le studio. `pieceId` (identifiant ou slug) quand on part de
 * l'œuvre, depuis une carte du catalogue : le serveur prend alors le fichier
 * de la personne si elle en a un, sinon celui que le catalogue sert. Une œuvre
 * peut réunir plusieurs fichiers, et chacun range, partage et rouvre le sien.
 */
export type ScoreTarget = { scoreId: string } | { pieceId: string };

/**
 * « Ajouter à ma bibliothèque », depuis une fiche du catalogue.
 *
 * Idempotent : le bouton peut être cliqué deux fois. Un `403` veut dire que la
 * pièce n'est ni publiée ni déjà à soi — on n'ajoute pas un brouillon qu'on n'a
 * pas le droit de voir.
 */
export async function addLibraryEntry(
  target: ScoreTarget,
): Promise<IdempotentWrite<LibraryEntryItem>> {
  const response = await fetch(`${API_BASE_URL}/api/library/entries`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(target),
  });
  await assertOk(response);

  return { created: response.status === 201, item: await response.json() };
}

// ── Library: Claim ────────────────────────────────────────────

export type ClaimLibraryResult = {
  requested: number;
  /** Le seul chiffre à montrer : ce qui est effectivement dans la liste. */
  entries: number;
  attached: number;
  missing: number;
};

/**
 * « Ces imports sont les miens. » Appelé juste après une inscription ou une
 * connexion, avec ce que le registre local a gardé.
 *
 * Idempotent côté serveur : rejouable tel quel si l'appel a échoué. C'est ce
 * qui autorise le front à ne vider son registre qu'une fois la réponse reçue.
 */
export async function claimLibrary(
  midiFileIds: readonly string[],
): Promise<ClaimLibraryResult> {
  const response = await fetch(`${API_BASE_URL}/api/library/claim`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ midiFileIds }),
  });
  await assertOk(response);

  return response.json();
}

// ── Progression du parcours ───────────────────────────────────

/** Les trois états qu'un morceau peut avoir, vus de « Ma bibliothèque ». */
export type PracticeState = "in_progress" | "played" | "revisit";

/**
 * Le résumé de progression, calculé par le client et rangé par le serveur.
 *
 * `journey_progress` reste un coffre : le serveur ne sait pas ce qu'est une
 * phrase et ne dérive rien de `data`. Ce résumé est le strict minimum indexable
 * qui permet à « Là où tu t'es arrêté » d'être une requête SQL plutôt qu'une
 * bibliothèque entière descendue au navigateur pour y être filtrée.
 *
 * La même forme sert dans les deux sens : c'est ce qu'on écrit avec `PUT
 * /api/progress/{id}` et ce que `GET /api/progress` relit. `phraseIndex` et
 * `phraseTotal` valent `null` hors `in_progress`, où ils n'ont rien à compter.
 *
 * Il se déduit d'un document de parcours par `journeySummaryFrom`
 * (`lib/library/journey-glance.ts`), seul endroit qui connaisse la règle.
 */
export type ProgressSummary = {
  readonly state: PracticeState;
  /** Rang de la phrase en cours, à partir de 1. */
  readonly phraseIndex: number | null;
  readonly phraseTotal: number | null;
};

/** Le résumé tel qu'une entrée de bibliothèque le porte : daté. */
export type LibraryPractice = ProgressSummary & {
  /** ISO 8601 : quand ce résumé a été poussé. */
  readonly updatedAt: string;
};

export type RemoteProgress = {
  morceauId: string;
  schemaVersion: number;
  /** ISO 8601. Sert à départager la copie serveur et la copie locale. */
  updatedAt: string;
  /** Le document `JourneyProgress`, opaque pour le serveur. */
  data: Record<string, unknown>;
  /**
   * `null` sur un document sauvegardé avant l'arrivée du résumé. C'est ce
   * `null` que `backfillProgressSummaries` rattrape.
   */
  summary: ProgressSummary | null;
};

/**
 * Toute la progression du compte, en un appel.
 *
 * Appelée à l'ouverture d'une session pour réhydrater un appareil qui ne
 * connaît pas encore ce compte. En un appel et non un par morceau : au moment
 * où l'on se connecte, on ne sait pas quels morceaux le compte porte.
 */
export async function listProgress(): Promise<{ items: RemoteProgress[] }> {
  const response = await fetch(`${API_BASE_URL}/api/progress`, {
    headers: authHeaders(),
  });
  await assertOk(response);

  return response.json();
}

/**
 * Sauvegarde la progression d'un morceau.
 *
 * Le serveur ne lit pas `data` : il le range. Toute la logique de parcours
 * reste ici, et c'est ce qui permet de changer sa forme sans migration.
 *
 * `summary` est explicite et non facultatif parce que `null` **efface** le
 * résumé précédent côté serveur. Un paramètre qu'on peut oublier effacerait
 * silencieusement l'état d'un morceau à la première sauvegarde d'un appelant
 * distrait ; ici, ne rien résumer est une décision qui s'écrit.
 */
export async function putProgress(
  morceauId: string,
  schemaVersion: number,
  data: unknown,
  summary: ProgressSummary | null,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/progress/${morceauId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    // `summary` omis plutôt que `null` : le serveur lit l'absence comme
    // l'effacement, et les deux écritures ont donc le même sens.
    body: JSON.stringify({ schemaVersion, data, summary: summary ?? undefined }),
  });
  await assertOk(response);
}

// ── Progression d'exercice ───────────────────────────────────

export type RemoteExerciseProgress = {
  /** L'identifiant VEF, pas l'UUID en base : c'est lui qui survit à un resync. */
  exerciseId: string;
  schemaVersion: number;
  updatedAt: string;
  /** Document opaque pour le serveur, comme pour les morceaux. */
  data: Record<string, unknown>;
};

/** Toute la progression d'exercice du compte, en un appel. */
export async function listExerciseProgress(): Promise<{
  items: RemoteExerciseProgress[];
}> {
  const response = await fetch(`${API_BASE_URL}/api/exercise-progress`, {
    headers: authHeaders(),
  });
  await assertOk(response);

  return response.json();
}

/**
 * Sauvegarde la progression d'un exercice.
 *
 * Comme pour les morceaux, le dernier écrit gagne et le serveur ne lit pas
 * `data`. L'identifiant passé est celui du fichier VEF.
 */
export async function putExerciseProgress(
  exerciseId: string,
  schemaVersion: number,
  data: unknown,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/exercise-progress/${exerciseId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ schemaVersion, data }),
    },
  );
  await assertOk(response);
}

// ── Library: Delete ───────────────────────────────────────────

/**
 * Retire une ligne de « Mes morceaux ».
 *
 * La ligne seulement : la pièce reste au catalogue et le fichier sur le disque.
 * C'est un geste de rangement : on perd le lien, pas le fichier. Il n'existe pas
 * d'autre suppression côté utilisateur.
 *
 * Rouvrir le morceau recrée la ligne — c'est l'ouverture qui l'écrit. Retirer
 * veut donc dire « pas maintenant », pas « plus jamais ».
 *
 * Le serveur sort aussi la pièce des playlists de la personne : une playlist
 * qui pointerait sur un morceau retiré afficherait une carte que son auteur ne
 * peut plus ouvrir. Un écran de playlist ouvert doit donc se recharger après ce
 * geste, sa liste ayant pu changer sans qu'il l'ait demandé.
 */
export async function deleteLibraryEntry(entryId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/library/entries/${entryId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  await assertOk(response);
}


// ── Partage par lien ──────────────────────────────────────────

/**
 * Un lien de partage, vu par celui qui l'a créé.
 *
 * Pas d'URL : le jeton seul. Le front assemble l'adresse depuis son propre
 * `window.location.origin` et `libraryShareLandingPath()`, parce qu'il est le
 * seul à savoir sous quel nom de domaine il est servi.
 */
export type ShareLink = {
  /** 32 caractères URL-safe. */
  token: string;
  pieceId: string;
  scoreId: string;
  createdAt: string;
  /** `null` tant que le lien est actif. */
  revokedAt: string | null;
  acceptedCount: number;
};

/**
 * L'aperçu que voit qui ouvre un lien de partage, connecté ou non.
 *
 * `alreadyInLibrary` est toujours faux en anonyme : le serveur n'a personne à
 * qui comparer. L'écran d'atterrissage doit donc rester juste sans lui.
 */
export type SharePreview = {
  token: string;
  /** `true` quand le lien a été retiré : le morceau se montre, l'ajout non. */
  revoked: boolean;
  sharedBy: { nickname: string | null };
  piece: CatalogPiece;
  /** Le fichier partagé, celui qu'on ouvre une fois le partage accepté. */
  scoreId: string;
  /**
   * Les silhouettes à grille fine, comme sur une fiche.
   *
   * L'atterrissage montre **un** morceau en grand : à 560 px de large, la
   * grille de carte (28 colonnes) donne des mailles de 20 px et la figure se
   * lit comme des barres au lieu d'un contour.
   *
   * `null` quand elles n'ont pas été calculées : on retombe alors sur
   * `piece.silhouette`, qui reste juste, seulement plus grossière.
   */
  silhouetteDetail: string | null;
  silhouetteTall: string | null;
  alreadyInLibrary: boolean;
  /**
   * Le morceau a été mis hors diffusion à la suite d'un signalement retenu.
   *
   * Distinct de `revoked`, qui ne parle que de ce lien-ci : ici c'est le
   * morceau qui ne circule plus, quel que soit le lien et quel que soit celui
   * qui l'a créé.
   */
  sharingBlocked: boolean;
  /**
   * Ce partage peut-il être signalé ?
   *
   * Faux sur une pièce du catalogue : ce n'est pas du contenu déposé par
   * quelqu'un, et une atteinte aux droits la concernant relève de la procédure
   * des mentions légales, par courrier. Le serveur tranche, l'écran ne devine
   * pas.
   */
  reportable: boolean;
};

/** Les motifs de signalement, miroir de `ShareReportReason` côté API. */
export type ShareReportReason = "copyright" | "illegal" | "other";

/**
 * Signale un partage depuis l'écran de son lien.
 *
 * Ouverte sans compte : l'aperçu d'un lien se sert sans compte, et les
 * conditions générales disent « toute personne peut signaler ». Le serveur
 * plafonne par adresse IP, seule protection possible quand il n'y a pas
 * d'identité à opposer.
 *
 * Ne rend rien : un signalement n'a pas de résultat immédiat à montrer, et
 * c'est voulu. Rien n'est bloqué automatiquement, sans quoi le bouton
 * deviendrait une arme.
 */
export async function reportShare(
  token: string,
  reason: ShareReportReason,
  message: string | null,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/shares/${token}/report`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ reason, message }),
  });
  await assertOk(response);
}

/**
 * Signale un partage depuis le morceau qu'on en a reçu.
 *
 * L'écran du lien ne reste pas : une fois le partage accepté, le morceau vit
 * dans « Mes morceaux » et le jeton n'est plus nulle part. C'est pourtant là
 * qu'on découvre, des jours plus tard, que ce qu'on a reçu ne devrait pas
 * circuler.
 */
export async function reportSharedEntry(
  entryId: string,
  reason: ShareReportReason,
  message: string | null,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/library/entries/${entryId}/report`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ reason, message }),
  });
  await assertOk(response);
}

/**
 * Crée le lien de partage d'un morceau, ou rend celui qui est déjà actif.
 *
 * Rejouer rend le **même** jeton : un second clic sur « partager » ne doit pas
 * multiplier les liens à révoquer un par un. `403` quand la pièce n'est pas
 * dans sa bibliothèque, parce qu'on ne partage que ce qu'on a.
 */
export async function createShareLink(
  target: ScoreTarget,
): Promise<IdempotentWrite<ShareLink>> {
  const response = await fetch(`${API_BASE_URL}/api/library/shares`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(target),
  });
  await assertOk(response);

  return { created: response.status === 201, item: await response.json() };
}

/**
 * Le lien actif de cette personne pour cette pièce, `null` s'il n'y en a pas.
 *
 * Le `204` est traduit en `null` et non en erreur : « pas encore de lien » est
 * une réponse, celle qui fait afficher « partager » plutôt que « lien déjà
 * créé ». C'est ce qui évite d'en créer un second pour le savoir.
 */
export async function getShareLink(target: ScoreTarget): Promise<ShareLink | null> {
  const query = new URLSearchParams(target);
  const response = await fetch(
    `${API_BASE_URL}/api/library/shares?${query}`,
    { headers: authHeaders() },
  );

  if (response.status === 204) {
    return null;
  }

  await assertOk(response);

  return response.json();
}

/**
 * Révoque un lien.
 *
 * Ne retire le morceau de la bibliothèque de personne : ceux qui l'ont accepté
 * gardent leur entrée, qui existe pour elle-même. Le lien cesse d'en créer de
 * nouvelles, c'est tout.
 */
export async function revokeShareLink(token: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/library/shares/${encodeURIComponent(token)}`,
    { method: "DELETE", headers: authHeaders() },
  );
  await assertOk(response);
}

/**
 * L'aperçu d'un lien, public.
 *
 * # Un `410` est une réponse, pas une panne
 *
 * Le serveur rend le **même corps** sur un lien révoqué : l'écran écrit « ce
 * lien a été retiré » au-dessus du morceau, ce qui est plus clair qu'une page
 * vide et ne divulgue rien de plus qu'un lien actif. Le laisser passer par
 * `assertOk` jetterait la seule chose qu'on avait à montrer. `revoked` porte
 * l'information, l'appelant n'a pas de statut HTTP à interpréter.
 *
 * `404`, lui, reste une erreur : il n'y a rien à afficher.
 *
 * L'en-tête d'authentification est envoyé **s'il y en a un**, jamais exigé :
 * c'est ce qui permet au serveur de renseigner `alreadyInLibrary` pour une
 * personne connectée, sans fermer l'aperçu à un visiteur.
 */
export async function previewShare(token: string): Promise<SharePreview> {
  const response = await fetch(
    `${API_BASE_URL}/api/shares/${encodeURIComponent(token)}`,
    { headers: authHeaders() },
  );

  if (response.status !== 410) {
    await assertOk(response);
  }

  return response.json();
}

/**
 * Accepte un lien : le morceau entre dans la bibliothèque, source `shared`.
 *
 * Idempotent — une pièce déjà présente est rendue telle quelle, sans que sa
 * source ni son `sharedBy` changent. `401` en anonyme, `410` si le lien a été
 * révoqué entre l'aperçu et le clic : les deux sont des `ApiError`, et leur
 * statut suffit à l'écran pour choisir quoi dire.
 */
export async function acceptShare(
  token: string,
): Promise<IdempotentWrite<LibraryEntryItem>> {
  const response = await fetch(
    `${API_BASE_URL}/api/shares/${encodeURIComponent(token)}/accept`,
    { method: "POST", headers: authHeaders() },
  );
  await assertOk(response);

  return { created: response.status === 201, item: await response.json() };
}

// ── Playlists ─────────────────────────────────────────────────

/**
 * Ce que toutes les réponses de playlist portent.
 *
 * Séparé parce que les trois formes ci-dessous n'ajoutent pas la même chose :
 * la liste rend une pochette, le détail rend son contenu, et l'ajout d'un
 * morceau ne rend ni l'un ni l'autre. Un seul type large obligerait chaque
 * appelant à se demander lesquels de ses champs sont vraiment remplis.
 */
export type PlaylistHeader = {
  id: string;
  name: string;
  description: string | null;
  pieceCount: number;
  createdAt: string;
  updatedAt: string;
};

/**
 * Une playlist telle que la liste l'affiche.
 *
 * `covers` porte les silhouettes des quatre premiers morceaux : une playlist
 * n'a pas d'image, et la seule matière visuelle honnête du produit est la
 * silhouette. Une case vaut `null` quand la pièce n'en a pas de calculée, et
 * la mosaïque rend alors un aplat neutre plutôt qu'une figure inventée.
 */
export type PlaylistSummary = PlaylistHeader & {
  covers: (string | null)[];
};

/**
 * Une ligne de playlist. Elle porte une **entrée de bibliothèque**, pas une
 * pièce : la carte rendue est celle de « Mes morceaux », état de travail
 * compris. L'entrée est toujours là, ajouter à une playlist la crée au besoin.
 */
export type PlaylistItem = {
  position: number;
  addedAt: string;
  entry: LibraryEntryItem;
};

export type PlaylistDetail = PlaylistHeader & {
  /** Dans l'ordre voulu, pas dans celui de l'ajout. */
  items: PlaylistItem[];
};

export async function listPlaylists(): Promise<{ items: PlaylistSummary[] }> {
  const response = await fetch(`${API_BASE_URL}/api/playlists`, {
    headers: authHeaders(),
  });
  await assertOk(response);

  return response.json();
}

/** `409` sur un nom déjà pris : ils sont uniques par personne. */
export async function createPlaylist(
  name: string,
  description: string | null = null,
): Promise<PlaylistSummary> {
  const response = await fetch(`${API_BASE_URL}/api/playlists`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ name, description }),
  });
  await assertOk(response);

  return response.json();
}

/** `404` sur une playlist qui n'est pas la sienne : elles sont personnelles. */
export async function getPlaylist(id: string): Promise<PlaylistDetail> {
  const response = await fetch(
    `${API_BASE_URL}/api/playlists/${encodeURIComponent(id)}`,
    { headers: authHeaders() },
  );
  await assertOk(response);

  return response.json();
}

/**
 * Renomme ou redécrit une playlist. Les deux champs sont facultatifs, et un
 * champ absent est laissé tel quel — d'où le `PATCH` : envoyer l'objet entier
 * obligerait l'appelant à relire ce qu'il ne veut pas toucher.
 */
export async function updatePlaylist(
  id: string,
  changes: { name?: string; description?: string | null },
): Promise<PlaylistSummary> {
  const response = await fetch(
    `${API_BASE_URL}/api/playlists/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(changes),
    },
  );
  await assertOk(response);

  return response.json();
}

/** Supprime la playlist, jamais les pièces ni les entrées de bibliothèque. */
export async function deletePlaylist(id: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/playlists/${encodeURIComponent(id)}`,
    { method: "DELETE", headers: authHeaders() },
  );
  await assertOk(response);
}

/**
 * Ajoute un morceau en fin de playlist, et crée l'entrée de bibliothèque si
 * elle manque : une playlist ne range que ce que son auteur peut ouvrir.
 *
 * Rend la playlist **avec son contenu**, dans la même forme que `getPlaylist` :
 * un écran qui vient d'ajouter un morceau affiche la liste à jour sans
 * enchaîner un second appel pour voir le résultat de son propre geste. Le type
 * disait `PlaylistHeader` du temps où la route répondait sans `items` ; elle
 * les sert désormais, et promettre moins que ce qui arrive obligeait l'écran de
 * détail à recharger pour rien.
 */
export async function addToPlaylist(
  playlistId: string,
  target: ScoreTarget,
): Promise<IdempotentWrite<PlaylistDetail>> {
  const response = await fetch(
    `${API_BASE_URL}/api/playlists/${encodeURIComponent(playlistId)}/items`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(target),
    },
  );
  await assertOk(response);

  return { created: response.status === 201, item: await response.json() };
}

/** Retire de la playlist, sans toucher à la bibliothèque. */
export async function removeFromPlaylist(
  playlistId: string,
  scoreId: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/playlists/${encodeURIComponent(playlistId)}/items/${encodeURIComponent(scoreId)}`,
    { method: "DELETE", headers: authHeaders() },
  );
  await assertOk(response);
}

/**
 * Réordonne. `scoreIds` doit contenir **exactement** les fichiers de la
 * playlist — un réordonnancement partiel serait ambigu sur le sort des absents,
 * et le serveur le refuse en `400`.
 *
 * Rend la pochette recalculée : les quatre premières silhouettes ont pu changer.
 */
export async function reorderPlaylist(
  playlistId: string,
  scoreIds: readonly string[],
): Promise<PlaylistSummary> {
  const response = await fetch(
    `${API_BASE_URL}/api/playlists/${encodeURIComponent(playlistId)}/items`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ scoreIds }),
    },
  );
  await assertOk(response);

  return response.json();
}

// ── Catalog (public pieces) ─────────────────────────────────

/**
 * Public piece catalog item. Backs the `/library` browse views. The
 * shape matches `PieceCatalogResponse` on the Symfony side.
 */
/**
 * Le statut de droits d'une pièce, miroir de `RightsStatus` côté API.
 *
 * Les quatre valeurs ne disent pas la même chose, et la nuance compte à
 * l'écran : `restricted` affirme que le morceau est sous droits, `unknown` dit
 * que **personne ne les a établis**. Un import est toujours `unknown` — la
 * personne peut aussi bien avoir déposé sa propre composition qu'un Bach
 * qu'elle a saisi elle-même. Écrire « sous droits » sur un `unknown`
 * affirmerait ce qu'on ignore.
 *
 * Seuls `public_domain` et `licensed` autorisent la publication : le doute ne
 * se résout pas en faveur de la mise en ligne.
 */
export type RightsStatus =
  | "unknown"
  | "public_domain"
  | "licensed"
  | "restricted";

export type CatalogPiece = {
  id: string;
  title: string;
  composer: string | null;
  genre: string | null;
  difficulty: number | null;
  /** Notoriété 1..5, absente sur un upload dont personne n'a mesuré l'audience. */
  popularity: number | null;
  tonality: string | null;
  bpm: number | null;
  durationSec: number | null;
  measureCount: number;
  /**
   * Nombre de notes du score primaire, `null` quand aucun score n'est
   * rattaché.
   *
   * C'est la seule mesure de *longueur de travail* du catalogue : `difficulty`
   * est la moyenne de la pire fenêtre de quatre mesures, donc aveugle à la
   * durée — un nocturne de six minutes en ressort au même niveau qu'une pièce
   * de trente secondes. Voir `lib/library/difficulty-tiers.ts`.
   */
  noteCount: number | null;
  /**
   * La silhouette du morceau, encodée. `null` quand le score n'a pas été
   * analysé — la carte rend alors un aplat neutre plutôt qu'une figure
   * inventée. Format et décodage : `lib/library/silhouette.ts`.
   */
  silhouette: string | null;
  /**
   * Le titre tel que la source l'a livré, quand il diffère de `title`.
   *
   * `title` porte déjà le titre relu (passe éditoriale côté serveur, avec repli
   * automatique) : il n'y a donc **rien à arbitrer ici**. Celui-ci ne sert qu'à
   * retrouver une pièce dans le catalogue d'origine, et ne s'affiche pas.
   */
  sourceTitle: string;
  /**
   * Le nom court et stable du morceau : `satie-gymnopedie-1`.
   *
   * C'est par lui que les parcours citent un morceau, et c'est lui qui servira
   * d'URL de fiche. L'identifiant, lui, ne survit pas à un rejeu du catalogue.
   * `null` sur une base dont la passe `app:catalog:slugs` n'a pas encore
   * rattrapé l'existant.
   */
  slug: string | null;
  /**
   * Ce que les mains doivent faire. `null` quand l'analyse n'a rien donné —
   * jamais 0, qui se lirait comme « aucune touche noire ».
   *
   * `handSpanMax` est l'écartement maximal d'une main en demi-tons, les deux
   * mains confondues : ce qui rend un morceau difficile, c'est le passage où il
   * faut le plus s'ouvrir, pas l'écartement habituel.
   */
  handSpanMax: number | null;
  /** Part de touches noires, en pourcentage entier. */
  blackKeyShare: number | null;
  /** Nombre de durées de note distinctes dans le morceau. */
  rhythmValues: number | null;
  hasScore: boolean;
  isNew: boolean;
  createdAt: string;
  publishedAt: string | null;
  /** Toujours `true` sur le catalogue public ; ne varie qu'en prévisualisation admin. */
  isPublic: boolean;
  rightsStatus: RightsStatus;
  /**
   * La licence de la **gravure**, telle qu'elle a été relevée à l'import.
   *
   * Une partition porte toujours deux couches de droits — l'œuvre et la
   * gravure — et `rightsStatus` résume leur conjonction, pas plus. Cette chaîne
   * dit la seconde, en clair, pour les écrans qui doivent rendre le crédit
   * exigible plutôt qu'un verdict : « CC0 1.0 », « Public Domain Mark 1.0 »,
   * « CC0 1.0 (gravure) ; œuvre sous droits ».
   *
   * `null` sur tout import : ne pas savoir est le défaut, et inventer une
   * licence serait pire que n'en afficher aucune.
   *
   * C'est de la **donnée**, pas de la copie : elle est relevée à l'import,
   * jamais rédigée. Un écran qui la rend la nettoie — voir
   * `lib/library/piece-rights.ts`.
   */
  license: string | null;
  /**
   * Ce que la détection de droits à l'import a conclu, en une phrase.
   *
   * « Œuvre sous droits : Yann Tiersen est né en 1970. », « Œuvre du domaine
   * public : Fryderyk Chopin est mort en 1849, protection expirée en 1919.
   * Gravure non établie. » — voir `docs/spec/droits-a-l-import.md`.
   *
   * C'est ce qui sépare les deux `unknown` : celui dont l'œuvre est libre mais
   * la gravure non établie, et celui dont on ne sait rien. La note dit sur
   * quelles dates le verdict s'appuie, ce qui permet à un humain de refaire le
   * calcul — la fiche est l'écran qui a la place de la porter.
   *
   * Facultatif : le champ est récent, et absent des réponses d'une API plus
   * ancienne. Un écran le lit toujours avec un repli.
   */
  /**
   * Ce qu'on a établi sur les droits, en clair.
   *
   * Porte la nuance que `rightsStatus` ne peut pas porter : un `unknown`
   * accompagné de « œuvre du domaine public, gravure non établie » ne dit pas la
   * même chose qu'un `unknown` nu. La carte n'a la place que d'un mot, la fiche
   * a celle de la phrase.
   *
   * C'est de la **donnée**, écrite par un script d'import et par des humains à
   * des dates différentes. Ne pas la rendre en supposant qu'elle est propre.
   */
  rightsNote: string | null;
  /**
   * Ce morceau est-il déjà dans ma bibliothèque ?
   *
   * Servi par `/api/pieces`, `/api/pieces/shelves` et la fiche. C'est ce qui
   * permet à une carte de catalogue de montrer « déjà chez toi » plutôt que de
   * proposer un ajout qui ne ferait rien.
   *
   * Toujours `false` en anonyme, et c'est juste : sans compte il n'y a pas de
   * bibliothèque, donc rien à signaler. La carte n'affiche alors aucun état,
   * plutôt qu'un « pas ajouté » qui promettrait un geste demandant d'abord un
   * compte.
   */
  inLibrary: boolean;
  /**
   * Les playlists de qui regarde qui contiennent cette pièce. Toujours vide en
   * anonyme, et vide sur les pages rendues côté serveur, qui sont servies sans
   * jeton.
   *
   * Sans lui, un menu « ajouter à une playlist » posé sur une carte de catalogue
   * afficherait des cases toutes décochées, y compris pour un morceau déjà
   * rangé : des coches qui mentent valent moins que pas de menu.
   */
  playlistIds: string[];
};

export type CatalogPieceDetail = {
  piece: CatalogPiece;
  /**
   * L'identifiant de mon entrée de bibliothèque pour cette pièce, `null` si je
   * ne l'ai pas.
   *
   * `piece.inLibrary` dit qu'elle existe, celui-ci permet de la **retirer**.
   * Sans lui, la fiche ne sait défaire que ce qu'elle vient elle-même
   * d'ajouter, et un morceau rangé lors d'une session précédente devient
   * irretirable depuis l'écran qui le montre.
   */
  libraryEntryId: string | null;
  arrangementId: string | null;
  scoreId: string | null;
  midiFileId: string | null;
  noteCount: number;
  source: string | null;
  arranger: string | null;
  description: string | null;
  /**
   * La sélection curée d'où vient le morceau et son rang — « Tes cinq premiers
   * morceaux · 1ᵉʳ ». `null` quand la pièce n'est dans aucune sélection curée,
   * c'est-à-dire pour la grande majorité du catalogue.
   */
  shelfLabel: string | null;
  /**
   * La phrase écrite à la main qui dit pourquoi ce morceau est dans sa sélection.
   *
   * C'est le même texte que `CatalogShelf.reasons[id]` sur l'accueil : la carte
   * l'affiche déjà, et la fiche est la destination de cette carte. Sans lui,
   * arriver sur la fiche *retire* quelque chose — d'où sa présence ici.
   *
   * `null` sur la grande majorité des pièces : la mise en page de la fiche doit
   * être juste sans lui, pas seulement avec.
   */
  shelfNote: string | null;
  /**
   * La silhouette à grille fine (56 × 42 contre 28 × 21), réservée à la fiche.
   *
   * Les deux surfaces ne peuvent pas partager une résolution : à 200 px de
   * large, 56 colonnes rendraient des marques de 2,4 px. À 460 px, 28 colonnes
   * écrasent le contour — 2,62 demi-tons par ligne contre 1,28, sur un ambitus
   * moyen de 52 demi-tons.
   *
   * `null` si elle n'a pas été calculée ; on retombe alors sur celle de la
   * carte, qui reste juste, seulement plus grossière.
   */
  silhouetteDetail: string | null;
  /**
   * La même figure en 4/3 fin (48 × 36), pour la fiche sur surface étroite.
   *
   * Les trois grilles servent trois surfaces, et aucune ne peut remplacer une
   * autre sans fausser les proportions — les mailles doivent rester carrées,
   * donc le rapport du cadre suit celui de la grille. À 390 px de large, un
   * bandeau 4:1 ferait 97 px de haut ; la grille de carte, elle, y donnerait
   * des mailles deux fois plus grosses que la même fiche en large.
   */
  silhouetteTall: string | null;
};

export type CatalogGenreFacet = {
  genre: string;
  count: number;
};

/**
 * Clés de tri du catalogue.
 *
 * `playability` est le tri de la découverte : `difficulty ASC, noteCount ASC,
 * popularity DESC, title ASC`. Il remonte d'abord ce qui est jouable, puis, à
 * jouabilité égale, ce qui est court, puis ce qui est connu — l'ordre exact
 * dans lequel quelqu'un qui débute choisit un morceau.
 */
export type CatalogSort =
  | "popularity"
  | "playability"
  | "duration"
  | "title"
  | "recent";

export type ListPiecesParams = {
  page?: number;
  limit?: number;
  search?: string;
  genre?: string;
  difficulty?: number;
  /** Seuil *minimum* de notoriété (1..5). Un seuil, pas une égalité. */
  popularity?: number;
  /** Seuil *maximum* de niveau (1..5), pour borner une sélection par le haut. */
  difficultyMax?: number;
  /** Durée maximale, en secondes. */
  durationMax?: number;
  /** Nombre de notes maximal du score primaire. */
  noteCountMax?: number;
  /** Facettes de geste, toutes en seuil **maximum**. */
  handSpanMax?: number;
  blackKeyShareMax?: number;
  rhythmValuesMax?: number;
  /** Défaut serveur : `popularity`. */
  sort?: CatalogSort;
  /**
   * Demande les pièces non publiées en plus du catalogue. Le serveur ne
   * l'honore que pour un admin et l'ignore en silence sinon — le front n'a
   * donc rien à savoir du rôle de l'utilisateur, et ne peut rien exposer que
   * l'API n'ait décidé d'exposer.
   */
  drafts?: boolean;
};

export async function listPieces(
  params: ListPiecesParams = {},
): Promise<PaginatedResult<CatalogPiece>> {
  const query = new URLSearchParams({
    page: String(params.page ?? 1),
    limit: String(params.limit ?? 24),
  });

  if (params.search) {
    query.set("search", params.search);
  }

  if (params.genre) {
    query.set("genre", params.genre);
  }

  if (params.difficulty !== undefined) {
    query.set("difficulty", String(params.difficulty));
  }

  if (params.popularity !== undefined) {
    query.set("popularity", String(params.popularity));
  }

  if (params.difficultyMax !== undefined) {
    query.set("difficultyMax", String(params.difficultyMax));
  }

  if (params.durationMax !== undefined) {
    query.set("durationMax", String(params.durationMax));
  }

  if (params.noteCountMax !== undefined) {
    query.set("noteCountMax", String(params.noteCountMax));
  }

  for (const key of [
    "handSpanMax",
    "blackKeyShareMax",
    "rhythmValuesMax",
  ] as const) {
    const value = params[key];

    if (value !== undefined) {
      query.set(key, String(value));
    }
  }

  if (params.sort !== undefined) {
    query.set("sort", params.sort);
  }

  if (params.drafts) {
    query.set("drafts", "1");
  }

  const response = await fetch(
    `${API_BASE_URL}/api/pieces?${query}`,
    { headers: authHeaders() },
  );
  await assertOk(response);

  return response.json();
}

export async function getPiece(id: string): Promise<CatalogPieceDetail> {
  const response = await fetch(
    `${API_BASE_URL}/api/pieces/${id}`,
    { headers: authHeaders() },
  );
  await assertOk(response);

  return response.json();
}

export async function listPieceGenres(
  drafts = false,
): Promise<{ items: CatalogGenreFacet[] }> {
  const response = await fetch(
    `${API_BASE_URL}/api/pieces/genres${drafts ? "?drafts=1" : ""}`,
    { headers: authHeaders() },
  );
  await assertOk(response);

  return response.json();
}

// ── Catalogue : les sélections de l'accueil ───────────────────

/**
 * Une sélection de l'accueil de la bibliothèque : un titre, une intention, les
 * premiers morceaux, et de quoi ouvrir la suite.
 *
 * `query` est ce qui évite de dupliquer les critères des deux côtés : le
 * serveur décide ce que « Tes 5 premiers morceaux » veut dire, le front se
 * contente de rejouer la requête telle quelle dans `listPieces()` pour ouvrir
 * la sélection en liste paginée. `null` marque une sélection curée à la main, qui ne se
 * rejoue pas — le front n'affiche alors pas de « voir les N ».
 */
export type CatalogShelf = {
  key: string;
  title: string;
  /** Phrase d'intention, affichée sous le titre. */
  intent: string;
  /** Effectif **total** de la sélection, dont `items` n'est que la tête. */
  total: number;
  items: CatalogPiece[];
  query: ListPiecesParams | null;
  /**
   * `true` quand la sélection est arrêtée à la main, `false` quand
   * c'est un tri. Une sélection curée n'a pas de `query` : elle montre tout ce
   * qu'elle a.
   */
  curated: boolean;
  /**
   * Identifiant de pièce → la phrase qui dit pourquoi elle est là. Vide sur un
   * sélection calculée — mais **toujours un dictionnaire**, jamais une liste, donc
   * `reasons[piece.id]` est sûr sans garde de forme.
   */
  reasons: Record<string, string>;
};

export type ShelvesResponse = { shelves: CatalogShelf[] };

/**
 * L'accueil de la bibliothèque en un appel.
 *
 * L'ordre des sélections est une décision éditoriale du serveur : on rend ce qu'on
 * reçoit, dans l'ordre reçu.
 */

/**
 * Une note de l'extrait jouable. Clés courtes parce que le serveur en envoie
 * quelques centaines et que ce tableau n'est pas lu par un humain : il est
 * enfilé dans un ordonnanceur.
 *
 * `t` et `d` sont en **millisecondes**, la carte de tempo ayant été intégrée
 * côté serveur — la Toccata porte soixante changements de tempo, et le front
 * n'a aucune connaissance musicale à avoir pour appuyer sur « écouter ».
 */
export type PreviewNote = {
  /** Départ, en ms depuis le début de l'extrait. */
  t: number;
  /** Durée, en ms. */
  d: number;
  /** Note MIDI, 0..127. */
  m: number;
  /** Vélocité normalisée, `]0, 1]`. */
  v: number;
};

export type PiecePreview = {
  startMs: number;
  durationMs: number;
  notes: PreviewNote[];
};

/**
 * Les vingt premières secondes d'un morceau.
 *
 * **Ne pas remplacer par `loadPieceSong`** malgré la tentation : cet
 * endpoint-là rend 1,63 Mio sur la Toccata contre 3,6 ko ici, et surtout il
 * **enregistre une ouverture de morceau** dans la bibliothèque de
 * l'utilisateur. Écouter n'est pas ouvrir — chaque écoute compterait comme une
 * lecture et fausserait les statistiques d'usage.
 *
 * Appelé à la demande, au premier clic sur « écouter » : un aperçu chargé au
 * montage ferait payer à chaque visiteur de fiche un extrait que la plupart
 * n'écouteront pas.
 */
export async function getPiecePreview(id: string): Promise<PiecePreview> {
  const response = await fetch(`${API_BASE_URL}/api/pieces/${id}/preview`, {
    headers: authHeaders(),
  });
  await assertOk(response);

  return response.json();
}

/**
 * Returns the full `ApiSongResponse` for one file of a piece (piece +
 * arrangement + score + analysis JSON) so the caller can hand it straight
 * to `createSongFromAnalysis()`.
 *
 * Sans `scoreId`, le serveur ouvre le fichier de la personne si elle en a un
 * dans sa liste, sinon celui que le catalogue sert.
 */
export async function loadPieceSong(
  id: string,
  scoreId?: string,
): Promise<ApiSongResponse> {
  const query = scoreId ? `?${new URLSearchParams({ score: scoreId })}` : "";
  const response = await fetch(
    `${API_BASE_URL}/api/pieces/${id}/song${query}`,
    { headers: authHeaders() },
  );
  await assertOk(response);

  return response.json();
}

// ── Programs: List ───────────────────────────────────────────

/** Intended working rhythm. The duration is derived from it, never declared. */
export type ProgramSession = {
  minutes: number;
  days_per_week: number;
  weekly_joker?: number;
};

/** À qui le contenu d'un parcours est ouvert. */
export type ProgramAccess = "public" | "authenticated" | "paid";

export type ProgramListItem = {
  id: string;
  vpfId: string;
  title: string;
  description: string;
  goal: string;
  level: number;
  /** vpfIds of the programs to finish first. Empty for entry programs. */
  prerequisites: string[];
  session: ProgramSession;
  isPublic: boolean;
  access: ProgramAccess;
  /** Vrai quand le contenu est fermé à ce visiteur. Le titre reste visible. */
  locked: boolean;
  pieceCount: number;
  exerciseCount: number;
  /**
   * De quoi savoir si ce parcours est fini, sans charger tous les détails.
   * Vide quand `locked` : un parcours qu'on ne peut pas ouvrir n'a pas de
   * progression à calculer.
   */
  gates: { midiFileId: string | null; stage: PieceStage | null }[];
  createdAt: string;
  updatedAt: string | null;
};

// ── Programs: Detail ────────────────────────────────────────

export type ExerciseCategory =
  | "fondations"
  | "rythme"
  | "gammes"
  | "arpeges"
  | "accords"
  | "independance"
  | "lecture"
  | "musicalite";

export type ExerciseStatus = "new" | "in_progress" | "mastered" | "review";

export type ProgramExerciseSummary = {
  id: string;
  /** Identifiant du fichier VEF, ce qui tient dans une URL lisible. */
  vefId: string | null;
  title: string;
  category: string | null;
  difficulty: number;
  duration: string;
  sectionCount: number;
  progress: {
    status: ExerciseStatus;
    bestScore: number | null;
  } | null;
};

/** How far into a piece's learning journey a milestone asks the learner to go. */
export type PieceStage = "S0" | "S1a" | "S1b" | "S2a" | "S2b" | "S3" | "S4" | "S5";

export type MilestoneGate =
  | { type: "piece_stage"; stage: PieceStage }
  | { type: "exercises_reached_stage"; stage: PieceStage; count?: number }
  | { type: "tempo_reached"; fraction: number };

/** An exercise a piece pulls behind it, with the reason it is there. */
export type ProgramRemedy = {
  vefId: string;
  because: string;
  /** Null when the exercise is gone from the library. */
  exercise: ProgramExerciseSummary | null;
};

export type ProgramPieceMilestone = {
  id: string;
  kind: "piece";
  position: number;
  pieceKey: string;
  /** Null when the piece left the library, in which case the milestone is hidden. */
  midiFileId: string | null;
  /** The library sheet is keyed by the piece, the journey by the MIDI file. */
  pieceId: string | null;
  /**
   * Le slug de la pièce résolue en base — pas `pieceKey`, qui est ce que le
   * fichier VPF a écrit. C'est lui qui adresse la fiche.
   */
  pieceSlug: string | null;
  pieceTitle: string | null;
  intent: string;
  targetStage: PieceStage;
  targetHands: "both" | "rh-only" | "lh-only" | null;
  reward: boolean;
  gate: MilestoneGate | null;
  review: number[];
  concepts: string[];
  remedies: ProgramRemedy[];
};

export type ProgramStepMilestone = {
  id: string;
  kind: "step";
  position: number;
  title: string;
  description: string | null;
  goal: string;
  gate: MilestoneGate | null;
  review: number[];
  concepts: string[];
  requires: string[];
  exercises: ProgramExerciseSummary[];
};

export type ProgramMilestone = ProgramPieceMilestone | ProgramStepMilestone;

export type ProgramDetail = {
  id: string;
  vpfId: string;
  title: string;
  description: string;
  goal: string;
  level: number;
  prerequisites: string[];
  session: ProgramSession;
  conceptsUnlocked: string[];
  isPublic: boolean;
  access: ProgramAccess;
  /** Vrai quand le contenu est fermé : `milestones` est alors vide. */
  locked: boolean;
  /** Les morceaux qu'on y joue. Servi même verrouillé, c'est la vitrine. */
  pieceTitles: string[];
  milestones: ProgramMilestone[];
  createdAt: string;
  updatedAt: string | null;
};

export async function getProgram(id: string): Promise<ProgramDetail> {
  const response = await fetch(`${API_BASE_URL}/api/programs/${id}`, {
    headers: authHeaders(),
  });
  await assertOk(response);

  return response.json();
}

// ── Programs: List ───────────────────────────────────────────

export async function listPrograms(
  page = 1,
  limit = 20,
): Promise<PaginatedResult<ProgramListItem>> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  const response = await fetch(
    `${API_BASE_URL}/api/programs?${params}`,
    { headers: authHeaders() },
  );
  await assertOk(response);

  return response.json();
}

// ── Exercises: Generate ───────────────────────────────────────

export type GenerateExerciseOptions = {
  prompt: string;
  bpm?: number;
  key?: string;
  measures?: number;
  timeSignature?: string;
  isPublic?: boolean;
};

export type ExerciseListItem = {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  difficulty: number;
  isPublic: boolean;
  averageRating: number | null;
  ratingCount: number;
  noteCount: number;
  createdAt: string;
  updatedAt: string | null;
};

export type ExerciseSection = {
  title: string;
  duration: string;
  completed: boolean;
};

export type ExerciseProgress = {
  status: ExerciseStatus;
  lastScore: number | null;
  bestScore: number | null;
  bestTempo: number | null;
  lastPracticed: string | null;
  completedSections: number;
  recommendedGoal: string | null;
} | null;

export type ExerciseDetail = {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  pedagogicalGoal: string | null;
  category: string | null;
  difficulty: number;
  duration: string | null;
  tags: string[];
  skills: string[];
  sections: ExerciseSection[];
  progress: ExerciseProgress;
  ownerId: string | null;
  ownerDisplayName: string | null;
  prompt: string;
  parameters: Record<string, unknown> | null;
  isPublic: boolean;
  averageRating: number | null;
  ratingCount: number;
  createdAt: string;
  updatedAt: string | null;
  userRating: number | null;
  analysisData: AnalysisResult;
};

export async function getExercise(id: string): Promise<ExerciseDetail> {
  const response = await fetch(`${API_BASE_URL}/api/exercises/${id}`, {
    headers: authHeaders(),
  });
  await assertOk(response);

  return response.json();
}

export async function generateExercise(
  options: GenerateExerciseOptions,
): Promise<ExerciseDetail> {
  const response = await fetch(`${API_BASE_URL}/api/exercises/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(options),
  });
  await assertOk(response);

  return response.json();
}

// ── Exercises: List ───────────────────────────────────────────

export async function listExercises(
  page = 1,
  limit = 20,
  sort: "rating" | "newest" = "newest",
): Promise<PaginatedResult<ExerciseListItem>> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sort,
  });

  const response = await fetch(
    `${API_BASE_URL}/api/exercises?${params}`,
    { headers: authHeaders() },
  );
  await assertOk(response);

  return response.json();
}

export async function listMyExercises(
  page = 1,
  limit = 20,
): Promise<PaginatedResult<ExerciseListItem>> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  const response = await fetch(
    `${API_BASE_URL}/api/exercises/mine?${params}`,
    { headers: authHeaders() },
  );
  await assertOk(response);

  return response.json();
}

// ── Exercises: Rate ───────────────────────────────────────────

export async function rateExercise(
  exerciseId: string,
  score: number,
): Promise<ExerciseDetail> {
  const response = await fetch(
    `${API_BASE_URL}/api/exercises/${exerciseId}/rate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify({ score }),
    },
  );
  await assertOk(response);

  return response.json();
}

// ── What's new: Release notes & feedback ─────────────────────

export type ReleaseNoteHorizon = "now" | "next" | "later";

export type ReleaseNote = {
  id: string;
  kind: "changelog" | "roadmap";
  title: string;
  description: string;
  horizon: ReleaseNoteHorizon | null;
  position: number;
  isPublished: boolean;
  releasedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
};

/** Shape of `GET /api/release-notes`, ready to paint the "Quoi de neuf" popin. */
export type ReleaseNotesPayload = {
  recent: ReleaseNote[];
  roadmap: Record<ReleaseNoteHorizon, ReleaseNote[]>;
  /** Newest changelog id; the front stores it to auto-open the popin once per release. */
  currentReleaseId: string | null;
};

export async function getReleaseNotes(): Promise<ReleaseNotesPayload> {
  const response = await fetch(`${API_BASE_URL}/api/release-notes`, {
    headers: authHeaders(),
  });
  await assertOk(response);

  return response.json();
}

/**
 * `session` n'est pas un troisième thème, c'est une provenance : un retour que
 * le produit est allé chercher en fin d'étape, là où `idea` et `bug` sont
 * apportés spontanément. Les mélanger fausserait la lecture des deux — voir
 * `FeedbackSubmission::TYPE_SESSION` côté API.
 */
export type FeedbackType = "idea" | "bug" | "session";

export type SubmitFeedbackInput = {
  type: FeedbackType;
  message: string;
  /** Optional contact email; pre-filled with the account email when signed in. */
  email?: string | null;
  /** Current route, attached for context. */
  pathname?: string | null;
  /** Newest release id seen by the client, attached as app version context. */
  appVersion?: string | null;
};

/**
 * Submit a feedback (idea / bug). Routes through the dedicated
 * `app/api/feedback` handler, which attaches the originating User-Agent
 * server-side — `fetch` would otherwise build its own.
 */
export async function submitFeedback(input: SubmitFeedbackInput): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/feedback`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(input),
  });
  await assertOk(response);
}

// ── Feature flags ──────────────────────────────────────────────

/**
 * Interrupteurs produit pilotés depuis l'admin. Clés déclarées côté API dans
 * `App\Domain\Feature\Feature` — le front n'en connaît que celles qu'il lit.
 */
export type FeatureFlags = {
  musescore_integration?: boolean;
};

/**
 * Endpoint public : pas de jeton, donc pas d'attente de session avant de
 * savoir quoi peindre.
 */
export async function getFeatures(): Promise<FeatureFlags> {
  const response = await fetch(`${API_BASE_URL}/api/features`);
  await assertOk(response);

  const payload: { features?: FeatureFlags } = await response.json();

  return payload.features ?? {};
}
